import { Link } from 'react-router-dom';

import HeroBackgroundCarousel from '../../components/HeroBackgroundCarousel';
import { usePublicFilieres } from '../../hooks/usePublicFilieres';
import { usePublicPartenairesSponsors } from '../../hooks/usePublicPartenairesSponsors';
import PublicSiteHeader from '../../components/public/PublicSiteHeader';
import LucideSymbolIcon from '../../components/icons/LucideSymbolIcon';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import PublicSiteFooter from '../../components/public/PublicSiteFooter';
import PublicPartnersLogoStrip from '../../components/public/PublicPartnersLogoStrip';
import PublicReveal, { publicRevealStagger } from '../../components/public/PublicReveal';
import PublicFiliereTracksCarousel from '../../components/public/PublicFiliereTracksCarousel';
import PublicProgrammeCarousel, {
  type ProgrammeItem,
} from '../../components/public/PublicProgrammeCarousel';
import { PublicProgrammeWaveBottom, PublicProgrammeWaveTop } from '../../components/public/home/PublicProgrammeWave';
import { PublicInscriptionWaveBottom, PublicInscriptionWaveTop } from '../../components/public/home/PublicInscriptionWave';
import { PublicTarifsWaveBottom } from '../../components/public/home/PublicTarifsWave';
import PublicInscriptionStepsCarousel from '../../components/public/PublicInscriptionStepsCarousel';
import { type StepId } from '../../components/public/PublicInscriptionStepIllustration';
import PublicTarifCategoriesCarousel from '../../components/public/PublicTarifCategoriesCarousel';
import PublicVacationsShowcase from '../../components/public/PublicVacationsShowcase';
import '../../styles/publicInscriptionSteps.css';
import '../../styles/publicSectionWave.css';
import { usePublicTarifs } from '../../hooks/usePublicTarifs';
type Props = {
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  /** Libellé du bouton espace connecté (ex. Administration / Mon espace). */
  spaceLabel?: string;
  onGoToSpace?: () => void;
};

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const HERO_BG_IMAGES = [
  `${PUBLIC_URL}/images/hero-banner-1.jpg`,
  `${PUBLIC_URL}/images/hero-banner-2.jpg`,
  `${PUBLIC_URL}/images/hero-banner-3.jpg`,
];

/** Visuel section « Le centre » (différent du slide 1 du hero pour variété). */
const ABOUT_CAMPUS_IMAGE = `${PUBLIC_URL}/images/hero-banner-4.jpg`;

/** Bannière section « Votre programme, concrètement » (style Microsoft 365). */
const PROGRAMME_BANNER_IMAGE = `${PUBLIC_URL}/images/hero-banner-5.jpg`;

const PROGRAMME_ITEMS: ProgrammeItem[] = [
  {
    id: 'cohorte',
    icon: 'calendar_month',
    title: 'Cohortes de 2 mois',
    description: 'Une cohorte de 2 mois pour le parcours que vous avez sélectionné.',
    href: '#filieres',
    linkLabel: 'Voir les filières',
  },
  {
    id: 'seances',
    icon: 'menu_book',
    title: '3 séances par semaine',
    description: 'Selon le planning officiel de votre filière (lun. · mer. · ven.).',
    href: '#vacations',
    linkLabel: 'Voir les vacations',
  },
  {
    id: 'vacation',
    icon: 'schedule',
    title: 'Vacations de 2 h',
    description: 'Chaque séance correspond à une vacation de 2 h, avec un créneau fixé à l’inscription.',
    href: '#vacations',
    linkLabel: 'Créneaux horaires',
  },
  {
    id: 'presence',
    icon: 'assignment_ind',
    title: 'Présences tracées',
    description: 'Présences enregistrées à chaque séance de votre cohorte.',
    href: '/login?mode=register',
    linkLabel: 'Créer un compte',
  },
  {
    id: 'evaluations',
    icon: 'school',
    title: 'Évaluations & certification',
    description: 'TP, examens, délibération et suivi des résultats pour votre groupe uniquement.',
    href: '#filieres',
    linkLabel: 'Programmes',
  },
  {
    id: 'paiement',
    icon: 'payments',
    title: 'Frais de participation',
    description: 'Frais liés à votre inscription, réglables en Mobile Money ou portefeuille.',
    href: '#tarifs',
    linkLabel: 'Voir les tarifs',
  },
];

/** Parcours : compte + OTP, choix formation + pièce d'identité, puis paiement à la souscription. */
const STEPS: Array<{ n: string; stepId: StepId; title: string; text: string }> = [
  {
    n: '01',
    stepId: 'account',
    title: 'Créer votre compte',
    text: 'Inscription avec e-mail et téléphone, puis validation par code OTP envoyé par e-mail. Aucun paiement à cette étape.',
  },
  {
    n: '02',
    stepId: 'formation',
    title: 'Choisir votre formation',
    text: "Connectez-vous, déposez votre pièce d'identité (validation admin pour confirmer votre catégorie), puis inscrivez-vous à une filière et une cohorte ouverte.",
  },
  {
    n: '03',
    stepId: 'payment',
    title: 'Régler les frais de souscription',
    text: "Payez en ligne les frais d'inscription, de participation et de brevet liés à votre inscription au cours (Mobile Money ou portefeuille).",
  },
  {
    n: '04',
    stepId: 'follow',
    title: 'Suivre votre parcours',
    text: 'Consultez votre planning, vos présences, notes, reçus et attestations depuis votre espace apprenant.',
  },
];

export default function AcademiaHomePage({ isAuthenticated, isAdmin, spaceLabel, onGoToSpace }: Props) {
  const { tracks, loading: loadingFilieres, error: filieresError, reload: reloadFilieres } = usePublicFilieres();
  const { partenaires, sponsors, loading: loadingPartenaires } = usePublicPartenairesSponsors();
  const { categories: tarifCategories, brevetHint, loading: loadingTarifs } = usePublicTarifs();
  return (
    <div className="min-h-screen overflow-x-clip bg-surface text-on-surface">
      <PublicSiteHeader
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        spaceLabel={spaceLabel}
        onGoToSpace={onGoToSpace}
      />

      <section className="relative overflow-hidden text-white">
        <HeroBackgroundCarousel images={HERO_BG_IMAGES} />
        {/* Rouge marque plus dense à gauche (lisibilité du texte) ; droite laissée claire pour voir le carrousel */}
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-primary/90 from-0% via-primary/60 via-[38%] sm:via-[36%] to-transparent to-[72%] sm:to-[68%] lg:to-[62%]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-[min(100%,42rem)] bg-gradient-to-t from-black/25 via-transparent to-transparent sm:w-[min(100%,48rem)]"
          aria-hidden
        />
        <div className="pointer-events-none absolute inset-0 opacity-[0.07]">
          <div className="absolute -left-10 top-1/4 h-72 w-72 -translate-y-1/2 rounded-full bg-primary-container blur-3xl" />
          <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-tertiary-container blur-3xl" />
        </div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
          <PublicReveal animation="fade-in-left">
          {/* <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">Kinshasa — RDC</p> */}
          <h1 className="mt-4 max-w-3xl text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Choisissez votre formation.  Inscrivez-vous en ligne.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/85">
            Cinq grands domaines de formation : parcourez-les en un coup d&apos;œil, ou ouvrez la page dédiée pour
            le programme complet de chaque formation.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#filieres"
              className="inline-flex items-center gap-2 rounded-public-btn bg-white px-6 py-3 text-sm font-bold text-primary shadow-lg hover:bg-white/95"
            >
              Voir toutes les formations
              <LucideSymbolIcon name="arrow_forward" tone="primary" size={18} />
            </a>
            <Link
              to="/login?mode=register"
              className="inline-flex items-center gap-2 rounded-public-btn border border-white/35 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              S&apos;inscrire sans choisir encore
            </Link>
          </div>
          </PublicReveal>
        </div>
      </section>

      <section
        id="apropos"
        className="public-section-before-programme-wave relative scroll-mt-20 overflow-x-clip border-t border-outline-variant/10 pt-20 sm:pt-28"
      >
        <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-primary/[0.07] blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute bottom-0 left-0 h-64 w-64 rounded-full bg-secondary/15 blur-3xl opacity-80" aria-hidden />

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <div className="flex flex-col gap-14 lg:gap-16">
            <div className="grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-12">
              <PublicReveal animation="fade-in-left" className="lg:col-span-5">
              <div>
                <h2 className="text-3xl font-black leading-[1.1] tracking-tight text-slate-900 sm:text-4xl lg:text-[2.35rem]">
                  Un campus numérique{' '}
                  <span className="relative inline-block">
                    <span className="relative z-10">intelligent</span>
                    <span
                      className="absolute -bottom-1 left-0 right-0 h-2.5 rounded-sm bg-primary/18 sm:h-3"
                      aria-hidden
                    />
                  </span>
                </h2>
                <p className="mt-6 text-base leading-relaxed text-on-surface-variant sm:text-lg sm:leading-relaxed">
                  JCONSULT MY Academia centralise suivi, évaluation et pilotage de votre parcours : pédagogie exigeante,
                  salles équipées et outils numériques pour une formation{' '}
                  <span className="font-semibold text-slate-800">transparente</span>, traçable et orientée emploi.
                </p>
                <div className="mt-8 flex flex-wrap gap-6 border-t border-outline-variant/20 pt-8">
                  <div>
                    <p className="text-2xl font-black tabular-nums text-primary sm:text-3xl">Kinshasa</p>
                    <p className="text-xs font-semibold uppercase tracking-widest text-on-surface-variant">Siège & campus</p>
                  </div>
                  <div className="hidden h-12 w-px bg-outline-variant/25 sm:block" aria-hidden />
                  <div>
                    <p className="text-2xl font-black tabular-nums text-slate-900 sm:text-3xl">100%</p>
                    <p className="text-xs font-semibold uppercase tracking-widest text-on-surface-variant">Parcours digitalisé</p>
                  </div>
                </div>
              </div>
              </PublicReveal>

              <PublicReveal animation="fade-in-right" className="relative lg:col-span-7">
              <div className="relative">
                <div className="pointer-events-none absolute -right-4 top-8 hidden h-32 w-32 rounded-full border border-primary/20 md:block" aria-hidden />
                <figure className="relative mx-auto max-w-xl lg:mx-0 lg:max-w-none">
                  <div className="relative aspect-[4/3] overflow-hidden rounded-public-card bg-slate-200 shadow-2xl shadow-slate-900/15 ring-1 ring-slate-900/10">
                    <img
                      src={ABOUT_CAMPUS_IMAGE}
                      alt="Apprenants et formateurs au sein du campus JCONSULT MY Academia à Kinshasa"
                      className="h-full w-full object-cover object-center transition duration-500 hover:scale-[1.02]"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-transparent" aria-hidden />
                  </div>
                  <figcaption className="mt-4 flex items-start gap-2.5 text-sm leading-snug text-on-surface-variant">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                      <LucideSymbolIcon name="location_on" tone="primary" size={18} />
                    </span>
                    <span>
                      <span className="font-semibold text-slate-800">Présentiel à Kinshasa</span>
                      {' — '}
                      salles équipées, encadrement rapproché et suivi numérique du premier jour jusqu&apos;à la
                      certification.
                    </span>
                  </figcaption>
                </figure>
              </div>
              </PublicReveal>
            </div>
          </div>
        </div>
      </section>

      <section id="programme" className="public-programme-wave-section scroll-mt-20" aria-label="Votre programme de formation">
        <PublicProgrammeWaveTop />
        <div className="public-section-wave__inner">
          <span className="public-section-wave__blob public-programme-wave-section__blob--1" aria-hidden />
          <span className="public-section-wave__blob public-programme-wave-section__blob--2" aria-hidden />
          <span className="public-section-wave__blob public-programme-wave-section__blob--3" aria-hidden />
          <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
            <PublicReveal animation="fade-in-up">
              <PublicProgrammeCarousel
                eyebrow="Organisation pédagogique"
                title="Votre programme, concrètement"
                subtitle="Une fois votre formation et votre cohorte choisies, votre parcours suit un calendrier précis : séances, vacations, évaluations et certification sont planifiées."
                bannerSrc={PROGRAMME_BANNER_IMAGE}
                bannerAlt="Organisation pédagogique et planning des formations JCONSULT MY Academia"
                items={PROGRAMME_ITEMS}
                exploreHref="#vacations"
                exploreLabel="Explorer le planning des vacations"
              />
            </PublicReveal>
          </div>
        </div>
        <PublicProgrammeWaveBottom />
      </section>

      <section
        id="filieres"
        className="public-section-after-wave public-section-surface-filieres relative scroll-mt-20 overflow-hidden py-16 sm:py-20"
      >
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <PublicReveal animation="fade-in-up">
            {loadingFilieres ? (
              <p className="text-center text-sm text-on-surface-variant">Chargement des filières…</p>
            ) : filieresError ? (
              <div className="text-center">
                <p className="text-sm text-on-surface-variant">Impossible de charger les formations.</p>
                <button
                  type="button"
                  onClick={() => void reloadFilieres()}
                  className="mt-4 rounded-public-btn bg-primary px-5 py-2.5 text-sm font-bold text-white"
                >
                  Réessayer
                </button>
              </div>
            ) : tracks.length === 0 ? (
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-primary">Formations</p>
                <h2 className="mt-2 text-3xl font-black text-slate-900 sm:text-4xl">Choisissez votre domaine</h2>
                <p className="mt-10 text-center text-sm text-on-surface-variant">
                  Aucun domaine actif pour le moment.
                </p>
              </div>
            ) : (
              <PublicFiliereTracksCarousel
                tracks={tracks}
                eyebrow="Formations"
                title="Choisissez votre domaine"
                subtitle="Puis sélectionnez la formation qui vous correspond."
              />
            )}
          </PublicReveal>
        </div>
      </section>

      <section
        id="parcours"
        className="public-inscription-wave-section scroll-mt-20"
        aria-label="Comment s'inscrire"
      >
        <PublicInscriptionWaveTop />
        <div className="public-section-wave__inner">
          <span className="public-section-wave__blob public-inscription-wave-section__blob--1" aria-hidden />
          <span className="public-section-wave__blob public-inscription-wave-section__blob--2" aria-hidden />
          <span className="public-section-wave__blob public-inscription-wave-section__blob--3" aria-hidden />
          <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
            <PublicReveal animation="fade-in-up">
              <p className="text-xs font-bold uppercase tracking-widest text-primary">Parcours</p>
              <h2 className="mt-2 text-3xl font-black text-slate-900 sm:text-4xl">Comment s&apos;inscrire ?</h2>
              <p className="mt-3 max-w-3xl text-on-surface-variant">
                Les étapes ci-dessous reprennent le parcours réel sur le portail et l&apos;application mobile : création de
                compte (OTP), choix de formation avec pièce d&apos;identité, puis paiement des frais à la souscription au cours.
              </p>
            </PublicReveal>
            <PublicReveal animation="fade-in-up" delay={100}>
              <PublicInscriptionStepsCarousel steps={STEPS} />
            </PublicReveal>
            <PublicReveal animation="fade-in-up" delay={450}>
              <div className="mt-10 text-center">
                <Link
                  to="/login?mode=register"
                  className="inline-flex items-center gap-2 rounded-public-btn bg-primary px-8 py-3 text-sm font-bold text-white shadow-md hover:opacity-90"
                >
                  Créer mon compte apprenant
                </Link>
              </div>
            </PublicReveal>
          </div>
        </div>
        <PublicInscriptionWaveBottom />
      </section>

      <section id="tarifs" className="public-section-after-wave public-tarifs-wave-section scroll-mt-20" aria-label="Tarification">
        <div className="public-section-wave__inner">
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <PublicReveal animation="fade-in-up">
              <PublicTarifCategoriesCarousel
                categories={tarifCategories}
                eyebrow="Tarification"
                title="Catégories de participants"
                subtitle={`Frais appliqués automatiquement selon votre profil. ${brevetHint}`}
                loading={loadingTarifs}
              />
            </PublicReveal>
          </div>
        </div>
        <PublicTarifsWaveBottom />
      </section>

      <PublicVacationsShowcase />

      <PublicPartnersLogoStrip
        partenaires={partenaires}
        sponsors={sponsors}
        loading={loadingPartenaires}
      />

      <PublicSiteFooter />

      <ScrollToTopButton />
    </div>
  );
}
